import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Download, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrivacyConsentModal() {
  const { user, logout, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [state, setState] = useState(null); // my-acceptance response
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.must_change_password) return;
    let active = true;
    api.get("/privacy-notice/my-acceptance")
      .then((r) => { if (active) setState(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [user]);

  const show = state?.required && !state?.already_accepted && state?.current_notice;
  if (!show) return null;

  const notice = state.current_notice;
  const pdfSrc = `${BACKEND_URL}${notice.pdf_url}`;
  const isFirstTime = true; // backend ya garantiza que no aceptó la versión vigente

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/privacy-notice/accept", { privacy_notice_id: notice.id });
      toast.success(t("privacyConsent.acceptedToast"));
      setState({ ...state, already_accepted: true });
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="privacy-consent-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="privacy-consent-modal"
        className="bg-white rounded-2xl border border-slate-200 w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFFBE6] border border-[#FFDF00] text-[#1A1A1A] flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-base text-[#1A1A1A]">
              {isFirstTime ? t("privacyConsent.titleFirst") : t("privacyConsent.titleNew")}
            </div>
            <div className="text-xs text-slate-500">
              {t("privacyConsent.versionLine", { n: notice.version_number, date: notice.effective_date })}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden p-5 space-y-4 flex flex-col">
          <p className="text-sm text-slate-600 leading-relaxed">{t("privacyConsent.body")}</p>
          <object data={pdfSrc} type="application/pdf" data-testid="privacy-consent-pdf"
            className="flex-1 w-full min-h-[280px] rounded-xl border border-slate-200 bg-slate-50">
            <div className="p-6 text-sm text-slate-600 text-center">
              {t("privacyConsent.pdfFallback")}
            </div>
          </object>
          <a href={pdfSrc} target="_blank" rel="noopener noreferrer" data-testid="privacy-consent-download"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A1A1A] hover:underline self-start">
            <Download className="w-4 h-4" /> {t("privacyConsent.downloadPdf")}
          </a>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input data-testid="privacy-consent-check" type="checkbox" checked={checked}
              onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FFDF00]" />
            <span className="text-sm text-slate-700">
              {t("privacyConsent.checkboxLabel", { n: notice.version_number, date: notice.effective_date })}
            </span>
          </label>
        </div>

        <div className="p-5 border-t border-slate-100 flex items-center justify-between gap-3">
          <button data-testid="privacy-consent-logout-btn" onClick={logout}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl">
            <LogOut className="w-4 h-4" /> {t("privacyConsent.logout")}
          </button>
          <button data-testid="privacy-consent-accept-btn" onClick={accept} disabled={!checked || busy}
            className="btn-primary-chizy px-6 py-2.5 rounded-xl text-sm disabled:opacity-50">
            {busy ? t("common.saving") : t("privacyConsent.acceptContinue")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
